import type { TileProps } from '../Tile';
import type { ViewStyle } from 'react-native';

import { tileConfigs } from '../Tile/Tile.util';

import { useMemo } from 'react';

export type ITile = Pick<TileProps, 'onPress' | 'primaryText' | 'secondaryText' | 'variant'>;

function useEstimatedDisplayWidth(
    estimatedTilesWidth: number,
    visibleLines: number,
    tileWidths: number[],
) {
    return useMemo(
        () => getEstimatedDisplayWidth(estimatedTilesWidth, visibleLines, tileWidths),
        [estimatedTilesWidth, visibleLines, tileWidths],
    );
}

export function useTilesEstimatedWidth(tiles: ITile[]): [number, number[]] {
    return useMemo(() => {
        let totalWidth = 0;
        const widths = [];
        for (const tile of tiles) {
            const width = calculateEstimatedTileWidth(tile);
            totalWidth += width;
            widths.push(width);
        }

        return [totalWidth, widths];
    }, [tiles]);
}

const CONTROL_STYLES = {
    button: {},
    tilesContainer: {},
};

export function useTruncationStyles(
    tiles: ITile[],
    visibleLines: number,
    isTruncatable: boolean,
): { button: ViewStyle; tilesContainer: ViewStyle } {
    // It is safe to early return in a hook if the hook is always called this way.
    // This is because React will see the same hook call stacks for every render.
    if (!isTruncatable) {
        return CONTROL_STYLES;
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [estimatedWidth, tileWidths] = useTilesEstimatedWidth(tiles);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const displayWidth = useEstimatedDisplayWidth(estimatedWidth, visibleLines, tileWidths);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const button = useTruncationToggleButtonStyles(displayWidth);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const tilesContainer = useTruncationTilesContainerStyles(displayWidth);

    return {
        button,
        tilesContainer,
    };
}

export function useTruncationToggleButtonStyles(estimatedWidth: number): ViewStyle {
    return useMemo(() => {
        const result = buildTruncationStyles(estimatedWidth, () => ({
            display: 'none',
        }));

        return result;
    }, [estimatedWidth]);
}

export function useTruncationTilesContainerStyles(estimatedWidth: number): ViewStyle {
    return useMemo(() => {
        const result = buildTruncationStyles(estimatedWidth, () => ({
            overflow: 'visible',
            maxHeight: undefined,
        }));

        return result;
    }, [estimatedWidth]);
}

function buildTruncationStyles(displayWidth: number, TextStyleBuilder: () => ViewStyle): ViewStyle {
    const remWidth = (Math.round(displayWidth * 100) / 100).toFixed(2);
    const mediaQuery = `@media screen and (min-width: ${remWidth}rem)`;
    const style = TextStyleBuilder();

    return {
        [mediaQuery]: style,
    };
}

export function calculateEstimatedTileWidth(tile: ITile): number {
    const paddingLeft = 0.5;
    const paddingRight = 0.5;
    // space to right of each tile
    const marginRight = 0.5;
    const iconsWidth = getEstimatedIconsWidth(tile);
    const textWidth = getEstimatedTextWidth(tile);

    return paddingLeft + paddingRight + marginRight + iconsWidth + textWidth;
}

function checkIfTilesFitDisplayWidth(
    tileWidths: number[],
    displayWidth: number,
    visibleLines: number,
) {
    let currentLineIndex = 0;
    let sum = 0;
    for (const tileWidth of tileWidths) {
        sum += tileWidth;
        if (sum > displayWidth) {
            sum = tileWidth;
            currentLineIndex += 1;
        }

        if (currentLineIndex >= visibleLines) {
            return false;
        }
    }

    return true;
}

function getEstimatedDisplayWidth(
    estimatedTilesWidth: number,
    visibleLines: number,
    tileWidths: number[],
) {
    // Start by dividing width by visibleLines. If all tiles can fit
    // within the visible lines, done. If not, add the width of
    // the last tile to total width
    // We want the display width getting bigger with each try.
    let clippedWidth = 0;
    let lastTileIndex = tileWidths.length - 1;
    let estimatedDisplayWidth = estimatedTilesWidth / visibleLines;
    while (checkIfTilesFitDisplayWidth(tileWidths, estimatedDisplayWidth, visibleLines) === false) {
        clippedWidth += tileWidths[lastTileIndex];
        estimatedDisplayWidth = (estimatedTilesWidth + clippedWidth) / visibleLines;
        lastTileIndex -= lastTileIndex;
    }

    return estimatedDisplayWidth;
}

// Icons are 18px x 18px.
function getEstimatedIconsWidth(tile: ITile): number {
    let width = 0;
    if (hasVariantIcon(tile)) {
        width += 1.125;
    }
    if (hasClickIcon(tile)) {
        width += 1.125;
    }

    return width;
}

export function getEstimatedTextWidth(tile: ITile): number {
    const text = tile.primaryText + (tile.secondaryText ?? '');
    const smallChars = ['i', 'l', "'", '.'];
    const space = ' ';
    let width = 0;

    for (const char of text) {
        if (char.toUpperCase() === char) {
            width += 0.5;
        } else if (smallChars.includes(char)) {
            width += 0.2;
        } else if (char === space) {
            width += 0.2;
        } else {
            width += 0.4;
        }
    }

    return width;
}

export function getTruncationContainerStyles(
    isTruncatable: boolean,
    isExpanded: boolean,
    visibleLines: number,
): ViewStyle {
    if (!isTruncatable || isExpanded) {
        return {};
    }
    // JMUI TODO review max height computations previously using rem
    // tile height + padding
    const baseFontSize = 16; // Assuming 1rem = 16px, adjust if needed
    const lineHeightPx = 2.8125 * baseFontSize;
    const maxHeightPx = visibleLines * lineHeightPx;

    return { overflow: 'hidden', maxHeight: maxHeightPx };
}

export function hasClickIcon(tile: ITile): boolean {
    return !!tile.onPress;
}

export function hasVariantIcon(tile: ITile): boolean {
    return !!tileConfigs[tile.variant]?.iconComponent;
}
